import { OwnOption } from '@cabonline/ui-web';
import { Role, useAuth, useUserListQueryV2 } from '@multiplatform-apps/service-trips';
import { MultiSelectOnChange, StoredUserFilters } from './types';
import { usePermissions } from '../hooks';
import { getUserSearchOverrides } from '../hooks/use-action-permissions';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { MultiValue } from 'react-select';
import { normalizeUserStatuses, UserStatusFilterValue } from '../utils/user-status-filter';

const PERMISSIONS_FALLBACK: {
  canCreateUser?: boolean;
  canUseSearchUsersButton?: boolean;
  canSeeAssignPassengersButton?: boolean;
} = {};

function resolvePermission(
  override: boolean | undefined,
  dataLoaded: boolean,
  permission: boolean | undefined,
  hasPermissionsObject: boolean,
): boolean {
  if (override !== undefined) return override;
  if (!dataLoaded) return false;
  if (!hasPermissionsObject) return true; // backward compat: no permissions object grants all
  return permission === true;
}

function resolveRequiredPermission(
  override: boolean | undefined,
  dataLoaded: boolean,
  permission: boolean | undefined,
): boolean {
  if (override !== undefined) return override;
  return dataLoaded && permission === true;
}

function normalizeStoredOptions(value: unknown): OwnOption[] {
  if (!Array.isArray(value)) return [];
  return value.filter((option): option is OwnOption => (
    typeof option === 'object' && option !== null &&
    (typeof option.value === 'string' || typeof option.value === 'number') &&
    typeof option.label === 'string'
  ));
}

function loadStoredFilters(): StoredUserFilters {
  const emptyFilters: StoredUserFilters = { selectedAgreement: [], selectedRole: [], selectedStatuses: [] };
  try {
    const savedFilters = localStorage.getItem('userFilters');
    if (!savedFilters) return emptyFilters;
    const stored: unknown = JSON.parse(savedFilters);
    if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) return emptyFilters;
    return {
      selectedAgreement: normalizeStoredOptions('selectedAgreement' in stored ? stored.selectedAgreement : undefined),
      selectedRole: normalizeStoredOptions('selectedRole' in stored ? stored.selectedRole : undefined),
      selectedStatuses: normalizeUserStatuses('selectedStatuses' in stored ? stored.selectedStatuses : undefined),
    };
  } catch {
    return emptyFilters;
  }
}

const isNoneRoleOption = (option: OwnOption | undefined): boolean => (
  typeof option?.value === 'string' && option.value === Role.None
);

const normalizeRoleSelection = (
  newValue: MultiValue<OwnOption>,
  actionMeta?: Parameters<MultiSelectOnChange>[1],
): MultiValue<OwnOption> => {
  const roles = [...newValue];
  const hasNoneRole = roles.some(isNoneRoleOption);

  if (!hasNoneRole || roles.length <= 1) {
    return newValue;
  }

  if (actionMeta?.action === 'select-option') {
    return isNoneRoleOption(actionMeta.option)
      ? roles.filter(isNoneRoleOption)
      : roles.filter((role) => !isNoneRoleOption(role));
  }

  return roles.filter((role) => !isNoneRoleOption(role));
};

function useUserFilters() {
  const [filters, setFilters] = useState<StoredUserFilters>(loadStoredFilters);

  useEffect(() => {
    try {
      if (!filters.selectedAgreement.length && !filters.selectedRole.length && !filters.selectedStatuses.length) {
        localStorage.removeItem('userFilters');
      } else {
        localStorage.setItem('userFilters', JSON.stringify(filters));
      }
    } catch {
      // Filtering still works in memory when browser storage is unavailable.
    }
  }, [filters]);

  const setSelectedAgreement = useCallback((newValue: MultiValue<OwnOption>) => {
    setFilters((previous) => ({ ...previous, selectedAgreement: newValue }));
  }, []);

  const setSelectedRole = useCallback((newValue: MultiValue<OwnOption>) => {
    setFilters((previous) => ({ ...previous, selectedRole: newValue }));
  }, []);

  const handleRoleChange = useCallback((newValue: MultiValue<OwnOption>, actionMeta?: Parameters<MultiSelectOnChange>[1]) => {
    setSelectedRole(normalizeRoleSelection(newValue, actionMeta));
  }, [setSelectedRole]);

  const handleStatusChange = useCallback<MultiSelectOnChange>((newValue) => {
    const selectedStatuses = normalizeUserStatuses(newValue.map((option) => option.value));
    setFilters((previous) => ({ ...previous, selectedStatuses }));
  }, []);

  const handleClearFilter = useCallback(() => {
    setFilters({ selectedAgreement: [], selectedRole: [], selectedStatuses: [] });
  }, []);

  return {
    ...filters,
    setSelectedAgreement,
    setSelectedRole,
    handleAgreementChange: setSelectedAgreement,
    handleRoleChange,
    handleStatusChange,
    handleClearFilter,
  };
}

function buildPermissions(
  overrides: ReturnType<typeof getUserSearchOverrides>,
  dataLoaded: boolean,
  perms: { canCreateUser?: boolean; canUseSearchUsersButton?: boolean; canSeeAssignPassengersButton?: boolean } | null | undefined
) {
  const o = overrides ?? PERMISSIONS_FALLBACK;
  const hasPermissionsObject = perms !== null && perms !== undefined;
  const p = perms ?? PERMISSIONS_FALLBACK;
  return {
    hasCreateUserPermission: resolvePermission(o.canCreateUser, dataLoaded, p.canCreateUser, hasPermissionsObject),
    hasSearchUserPermission: resolvePermission(o.canUseSearchUsersButton, dataLoaded, p.canUseSearchUsersButton, hasPermissionsObject),
    hasAssignPassengerPermission: resolveRequiredPermission(o.canSeeAssignPassengersButton, dataLoaded, p.canSeeAssignPassengersButton),
  };
}

function useUserPermissions() {
  const { isAuthenticated } = useAuth();
  const { hasRootRoutePermission } = usePermissions();
  const canAccessUsersPage = hasRootRoutePermission('usersPage');

  const {
    data: searchData,
    isLoading: isQueryLoading,
    error: queryError,
  } = useUserListQueryV2(
    { page: 0, size: 1 },
    { enabled: isAuthenticated && canAccessUsersPage }
  );

  const userSearchOverrides = getUserSearchOverrides();
  const hasAssignOverride =
    userSearchOverrides?.canSeeAssignPassengersButton !== undefined;
  const hasUsablePermissionData = searchData !== undefined || hasAssignOverride;

  return {
    isLoadingPermissions: isAuthenticated && isQueryLoading,
    isLoadingAssignPermission: !hasAssignOverride && isAuthenticated && isQueryLoading,
    permissionsError: hasUsablePermissionData ? null : queryError ?? null,
    ...buildPermissions(userSearchOverrides, searchData !== undefined, searchData?.permissions),
  };
}

interface UsersContextType {
  selectedAgreement: MultiValue<OwnOption>;
  setSelectedAgreement: (newValue: MultiValue<OwnOption>) => void;
  handleAgreementChange: MultiSelectOnChange;
  selectedRole: MultiValue<OwnOption>;
  setSelectedRole: (newValue: MultiValue<OwnOption>) => void;
  handleRoleChange: MultiSelectOnChange;
  selectedStatuses: readonly UserStatusFilterValue[];
  handleStatusChange: MultiSelectOnChange;
  handleClearFilter: () => void;
  isLoadingPermissions: boolean;
  isLoadingAssignPermission: boolean;
  permissionsError: Error | null;
  hasCreateUserPermission: boolean;
  hasSearchUserPermission: boolean;
  hasAssignPassengerPermission: boolean;
}

export const UsersContext = React.createContext<UsersContextType>(
  {} as UsersContextType
);

export const UsersProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const {
    selectedAgreement,
    setSelectedAgreement,
    selectedRole,
    setSelectedRole,
    handleAgreementChange,
    handleRoleChange,
    selectedStatuses,
    handleStatusChange,
    handleClearFilter,
  } = useUserFilters();

  const {
    isLoadingPermissions,
    isLoadingAssignPermission,
    permissionsError,
    hasCreateUserPermission,
    hasSearchUserPermission,
    hasAssignPassengerPermission,
  } = useUserPermissions();

  const value = useMemo(
    () => ({
      selectedAgreement,
      setSelectedAgreement,
      selectedRole,
      setSelectedRole,
      handleRoleChange,
      selectedStatuses,
      handleStatusChange,
      handleAgreementChange,
      handleClearFilter,
      isLoadingPermissions,
      isLoadingAssignPermission,
      permissionsError,
      hasCreateUserPermission,
      hasSearchUserPermission,
      hasAssignPassengerPermission,
    }),
    [selectedAgreement, setSelectedAgreement, selectedRole, setSelectedRole, selectedStatuses, handleStatusChange, handleRoleChange, handleAgreementChange, handleClearFilter, isLoadingPermissions, isLoadingAssignPermission, permissionsError, hasCreateUserPermission, hasSearchUserPermission, hasAssignPassengerPermission]
  );

  return (
    <UsersContext.Provider value={value}>
      {children}
    </UsersContext.Provider>
  );
};

export const useUsers = () => React.useContext(UsersContext);
